import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react'
import { Button, Modal, Form, Divider, Message } from 'semantic-ui-react'
import WeekDaysSelect from '../../../components/shared/WeekDaysSelect'
import { DatePicker } from '../../../components/shared/DatePicker'
import StatisticSourcesDropDown from "../../../components/shared/StatisticSourcesDropDown";
import { useMessageResources, useTextResources } from '../../../language'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  makeIntToDaysOfWeekArray,
  makeDaysOfWeekArrayIntoInt,
  getCollisionType,
  validateDateRange,
  validateDateRangeMessage,
} from '../../../components/shared/utils'
import DropDown from '../../../components/shared/dropdown'
import {
  DiscountApplicationTypes,
  DiscountApplicationTypesEnum,
  Designations,
  DiscountTypes,
  DiscountTypesEnum,
  RateDiscountTypesEnum,
  DiscountPropertiesEnum,
  maxNumericInputValue,
  ERateCategory,
  ApplyLrvFilterTypesEnum,
  ApplyLrvFilterTypes,
  ForbiddenCharacters
} from '../../../components/shared/constants'
import CheckBox from '../../../components/shared/Checkbox'
import NumericInput from '../../../components/shared/NumericInput'
import CheckBoxToggle from '../../../components/shared/CheckBoxToggle'
import { ExtraPersonPriceEdit } from '../Pricing/ExtraPersonPriceEdit'
import {
  adultsDesignations,
  childrenDesignations,
  infantsDesignations,
} from '../consts'
import { Rates } from '../../../api/services/next'
import {
  onError,
  onSuccess,
  onWarning,
} from '../../../components/shared/messages'
import { PriceListContext } from '../context'
import RatesDropDown from '../../../components/shared/RatesDropDown'
import HotelsDropDown from '../../../components/shared/HotelsDropDown'
import TextInput from '../../../components/shared/TextInput'
import { useDateTimeRange } from '../../../hooks/useDateTimeRange'
import ClassificationCategoriesDropDown from '../../../components/shared/ClassificationCategoriesDropDown'
import PriceGroupsDropDown from '../../../components/shared/PriceGroupsDropDown'
import { SyncRateToChannelManager } from '../../../api/services/pms'
import { useLoader } from '../../../hooks/useLoader';
import ReservationPoliciesDropDown from "../../../components/shared/ReservationPoliciesDropDown";

export const DiscountPopup = ({
  open,
  onClose,
  onSaveComplete,
  discountForEdit,
  rateDiscountTypeToCreate: rateDiscountTypeValue,
  isDiscountTypeDisabledBasedOnOtherRateDiscounts,
  existingRateDiscounts,
}) => {
  const text = useTextResources()
  const message = useMessageResources()
  const loader = useLoader()
  const { state } = useContext(PriceListContext)
  const [discount, setDiscount] = useState({})
  const isCreate = !discountForEdit?.Id
  const [isEdit, setIsEdit] = useState(!isCreate)
  const { startDate, endDate, setStartDate, setEndDate } = useDateTimeRange({
    clearable: false,
  })
  const [selectedDays, setSelectedDays] = useState(null)
  const [discountType, setDiscountType] = useState(DiscountTypes.Amount)
  const [discountApplication, setDiscountApplication] = useState(
    DiscountApplicationTypesEnum.Product
  )
  const [rateDiscountType, setRateDiscountType] = useState(
    RateDiscountTypesEnum.OverallDiscount
  )
  const [repeatEveryLabel, setRepeatEveryLabel] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [selectedHotel, setSelectedHotel] = useState(null)
  const [selectedRates, setSelectedRates] = useState([])

  const discountApplicationTypesDropDownData = useMemo(
    () => [
      { Id: 1, Name: text(DiscountApplicationTypes[1]) },
      { Id: 2, Name: text(DiscountApplicationTypes[2]) },
      { Id: 3, Name: text(DiscountApplicationTypes[3]) },
    ],
    []
  )

  const discountTypes = useMemo(
    () => [
      {
        Id: 1,
        Name: text(DiscountTypes[1]),
        disabled:
          isCreate &&
          isDiscountTypeDisabledBasedOnOtherRateDiscounts?.(
            DiscountTypesEnum.Amount
          ),
      },
      {
        Id: 2,
        Name: text(DiscountTypes[2]),
        disabled:
          isCreate &&
          isDiscountTypeDisabledBasedOnOtherRateDiscounts?.(
            DiscountTypesEnum.Percent
          ),
      },
    ],
    [isCreate, isDiscountTypeDisabledBasedOnOtherRateDiscounts]
  )

  useEffect(() => {
    if (discountForEdit) {
      if (isDiscountTypeDisabledBasedOnOtherRateDiscounts) {
        setDiscountType(
          isDiscountTypeDisabledBasedOnOtherRateDiscounts?.(
            DiscountTypesEnum.Amount
          )
            ? DiscountTypesEnum.Percent
            : DiscountTypesEnum.Amount
        )
      } else {
        setDiscountType(
          discountForEdit?.RateDiscountAmounts?.find(
            (rda) => (!discount?.RateDiscountAssignments && rda.DiscountPercent && rda.DiscountPercent >= -100) || rda.DiscountPercent > 0
          ) != null
            ? DiscountTypesEnum.Percent
            : DiscountTypesEnum.Amount
        )
      }
      setRateDiscountType(discountForEdit?.RateDiscountTypeId)
      setStartDate(moment(discountForEdit.StartDate).toDate())
      setEndDate(moment(discountForEdit.EndDate).toDate())
      setSelectedDays(makeIntToDaysOfWeekArray(discountForEdit.Weekdays))
      setDiscount(discountForEdit)
      setSelectedRates(discountForEdit?.RateDiscountAssignments?.map(x => x.RateId) || [])
      setSelectedHotel(
        discountForEdit?.Rates?.find((rate) => rate.HotelId)?.HotelId || state?.HotelId
      )
      setDiscountApplication(discountForEdit?.DiscountApplicationTypeId)
      setIsEdit(!isCreate)
    }
  }, [
    discountForEdit,
    isCreate,
    isDiscountTypeDisabledBasedOnOtherRateDiscounts,
    state?.HotelId
  ])

  useEffect(() => {
    setRateDiscountType(rateDiscountTypeValue)
  }, [rateDiscountTypeValue])


  useEffect(() => {
    setRepeatEveryLabel(
      text('RepeatEveryX').replace('{0}', `${discount?.ApplicableNight}`)
    )
  }, [discount?.ApplicableNight])

  const changeDiscountField = useCallback((fieldName, value) => {
    setDiscount((prev) => ({ ...prev, [fieldName]: value }))
  }, [])

  const currentDiscountType = useMemo(() => {
    if (discountType === DiscountTypesEnum.Amount) {
      if (discount?.RateDiscountAssignments) {
        return discount?.PriceIncludesTax
          ? DiscountPropertiesEnum.DiscountAmountAfterTax
          : DiscountPropertiesEnum.DiscountAmountBeforeTax
      } else {
        return state?.IsProductPricingIncludesTax
          ? DiscountPropertiesEnum.DiscountAmountAfterTax
          : DiscountPropertiesEnum.DiscountAmountBeforeTax
      }
    } else {
      return DiscountPropertiesEnum.DiscountPercent
    }
  }, [
    discountType,
    discount?.RateDiscountAssignments,
    discount?.PriceIncludesTax,
    state?.IsProductPricingIncludesTax,
  ])

  const currentMaxInputValue = useMemo(() => {
    return currentDiscountType === DiscountPropertiesEnum.DiscountPercent
      ? 100
      : maxNumericInputValue
  }, [currentDiscountType])

  const currentMinInputValue = useMemo(() => {
    if (discount?.RateDiscountAssignments?.length) {
      return 0;
    }

    return currentDiscountType === DiscountPropertiesEnum.DiscountPercent
      ? -100
      : -maxNumericInputValue;
  }, [currentDiscountType, discount?.RateDiscountAssignments])

  const discountTypeChanged = useCallback(
    (value) => {
      changeDiscountField('RateDiscountAmounts', [])
      setDiscountType(value)
    },
    [changeDiscountField]
  )

  const ratesChanged = useCallback((values) => {
    setDiscount((prev) => ({
      ...prev,
      RateDiscountAssignments: values?.map((x) => ({ RateId: x })) || [],
    }))
    setSelectedRates(values || [])
  }, [])

  const getPriceByDesignationId = useCallback(
    (designationId) => {
      return (
        discount?.RateDiscountAmounts?.find(
          (da) => da.DesignationId === designationId
        )?.[currentDiscountType]
      )
    },
    [currentDiscountType, discount?.RateDiscountAmounts]
  )

  const changeDesignatedPrice = useCallback(
    (designationId, value) => {
      if (!discount?.RateDiscountAmounts) return

      const discountAmount = discount.RateDiscountAmounts.find(x => x.DesignationId === designationId);

      if ((value === null || value === undefined) && !discountAmount) return;

      let updatedDiscountAmounts = discount.RateDiscountAmounts;

      if ((value === null || value === undefined) && discountAmount) {
        updatedDiscountAmounts = updatedDiscountAmounts.filter(da => da.DesignationId !== discountAmount.DesignationId);
      }
      else if (Number.isFinite(value) && discountAmount) {
        discountAmount[currentDiscountType] = value;
        if (discountType === DiscountTypesEnum.Amount) {
          discountAmount.DiscountAmount = value;
        }
        else {
          discountAmount.DiscountPercent = value;
        }
      }
      else if (Number.isFinite(value) && !discountAmount) {
        const isOverallUpdate = designationId === Designations.Overall

        if (isOverallUpdate) {
          updatedDiscountAmounts = updatedDiscountAmounts.filter(da => da.DesignationId === Designations.Overall);
        }
        else {
          updatedDiscountAmounts = updatedDiscountAmounts.filter(da => da.DesignationId !== Designations.Overall);
        }

        updatedDiscountAmounts.push({
          RateDiscountId: discount.Id,
          DesignationId: designationId,
          [currentDiscountType]: value,
          DiscountAmount:
            discountType === DiscountTypesEnum.Amount ? value : null,
          DiscountPercent:
            discountType === DiscountTypesEnum.Percent ? value : null,
        });
      }

      // Apply the updated discount amounts back to the discount object
      changeDiscountField('RateDiscountAmounts', updatedDiscountAmounts)
    },
    [
      discount?.RateDiscountAmounts,
      discount?.Id,
      changeDiscountField,
      currentDiscountType,
      discountType,
    ]
  )
  const isMinGreaterThanMax = (discount) =>
    parseInt(discount.LeadFixedDaysStart) > parseInt(discount.LeadFixedDaysEnd)
    ||
    parseInt(discount.MinimumNumberOfNights) > parseInt(discount.MaximumNumberOfNights)

  const handleSave = useCallback(async () => {
    if (discount?.RateDiscountAssignments && !discount?.Name) {
      onWarning(message('RatePlanValidationNameIsRequired'));
      return false;
    }

    if (discount?.Name && ForbiddenCharacters.some(c => discount?.Name.includes(c))) {
      onWarning(message('PromotionNameContainsForbiddenCharacters'));
      return false;
    }

    if (discount?.RateDiscountAssignments && !discount?.RateDiscountAssignments.length) {
      onWarning(message('RateIsRequired'));
      return false;
    }

    if (
      !discount?.RateDiscountAmounts?.length ||
      ((discount?.RateDiscountAssignments && !discount?.RateDiscountAmounts?.find((da) => da.DiscountAmount > 0) &&
        !discount?.RateDiscountAmounts?.find((da) => da.DiscountPercent > 0)) ||
        (!discount?.RateDiscountAssignments && !discount?.RateDiscountAmounts?.find((da) => da.DiscountPercent && da.DiscountPercent >= -100) &&
          !discount?.RateDiscountAmounts?.find((da) => da.DiscountAmount && da.DiscountAmount >= -maxNumericInputValue)))
    ) {
      onWarning(message('MustfillInAtLeastOneValueOfAPercentageAmount'))
      return false
    }

    const minAndMaxInValid = isMinGreaterThanMax(discount)
    if (minAndMaxInValid) {
      onWarning(message('MaximumBiggerThanMinimum'))
      return false
    }

    if (existingRateDiscounts?.length) {
      const hasOverlappingDates =
        existingRateDiscounts
          .filter(x => x.Id !== discount.Id && x.IsActive)
          .map(x => getCollisionType(x, { StartDate: startDate, EndDate: endDate, Weekdays: selectedDays }))
          .some(x => x !== 0);

      if (hasOverlappingDates) {
        onWarning(message('ErrorOverlappingDates'))
        return false
      }
    }

    if (!isEdit &&
      discount?.IsMobile &&
      discount?.RateDiscountAssignments &&
      discount?.RateDiscountTypeId === RateDiscountTypesEnum.OverallDiscount
    ) {
      try {
        const existingMobilePromotion = await Rates.GetRateDiscounts({
          HotelId: selectedHotel,
          IsMobile: true
        });

        if (existingMobilePromotion?.some(d => d.Id !== discount?.Id)) {
          onWarning(message('MobilePromotionExists'));
          return false;
        }
      } catch (error) {
        console.error(error);
        return false;
      }
    }

    if (discount?.Name) {
      try {
        const existingDiscountsWithSameName = await Rates.GetRateDiscounts({
          HotelId: selectedHotel,
          Name: discount.Name
        });

        const isOtherDiscountWithSameNameExist = existingDiscountsWithSameName.some(d => d.Id !== discount?.Id);
        if (isOtherDiscountWithSameNameExist) {
          onWarning(message('PromotionWithTheSameNameExists'));
          return false;
        }
      } catch (error) {
        console.error('Name validation failed:', error);
        onError(message('ValidationError'));
        return false;
      }
    }

    let discountForSave = { ...discount }
    discountForSave.Weekdays = makeDaysOfWeekArrayIntoInt(selectedDays)
    discountForSave.StartDate = moment(
      moment(startDate).toDate().toISOString()
    ).toISOString(true)
    discountForSave.EndDate = moment(
      moment(endDate).toDate().toISOString()
    ).toISOString(true)
    if (!Object.hasOwn(discountForSave, 'IsActive') || discountForSave.IsActive === null) {
      discountForSave.IsActive = true;
    }

    try {
      setIsLoading(true);
      loader(true);

      var newRateDiscounts = await Rates.SaveRateDiscounts([discountForSave]);
      if (newRateDiscounts.length == 1 && !isEdit) {
        setDiscount((prev) => ({ ...prev, ...newRateDiscounts[0] }));
        setIsEdit(true);
      }

      const isPromotion = discountForSave?.RateDiscountAssignments?.length > 0;

      const currentOrNewDiscountId = discountForSave.Id || newRateDiscounts?.[0]?.Id; //-- promotion id
      if (currentOrNewDiscountId) {
        await SyncRateToChannelManager({
          PMSHotelID: selectedHotel,
          ExternalPMSRateID: isPromotion ? currentOrNewDiscountId : discountForSave.RateId,
          eRateCategory: isPromotion ? ERateCategory.MultiRateDiscount : ERateCategory.FullRate
        })
      }

      onSuccess()
      await onSaveComplete(discountForSave)
      return true
    } catch (error) {
      onError(error)
    } finally {
      loader(false)
      setIsLoading(false)
    }

    return false
  }, [discount, endDate, existingRateDiscounts, onSaveComplete, selectedDays, selectedHotel, startDate])

  const handleClose = useCallback(() => {
    setSelectedDays(null)
    setDiscount({})
    setSelectedRates([])
    setSelectedHotel(null)
    setIsEdit(null)
    onClose()
  }, [onClose])

  const handleSaveAndClose = useCallback(async () => {
    const isSaved = await handleSave()
    if (isSaved) {
      handleClose()
    }
  }, [handleClose, handleSave])

  const memoDates = useMemo(() => startDate && endDate ? [{ startDate, endDate }] : [], [startDate, endDate]);

  const ratesSpec = useMemo(() => ({ HotelId: selectedHotel, InheritedRates: true }), [selectedHotel])

  const reservationPolicySpec = useMemo(() => ({
    GetOnlyParentPolicies: true
  }), [])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className='discount-popup'
      dimmer
      centered
    >
      <Modal.Header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {`${isCreate ? text('Create') : text('Update')}
            ${rateDiscountType == RateDiscountTypesEnum.DiscountForEveryXNight
            ? text('DiscountForEveryXNight')
            : !discount?.RateDiscountAssignments
              ? text('OverallDiscount')
              : `${text('Overall')} ${text('Promotion')}`
          } `}{' '}
        <Button circular icon='close' onClick={handleClose} />
      </Modal.Header>
      <Modal.Content className='modal-content-scrollable'>
        <Form warning={!validateDateRange(startDate, endDate, selectedDays)} className={isCreate || discountForEdit.IsActive ? '' : 'disabled'}>
          {discount?.RateDiscountAssignments && (
            <Form.Group className='discount-form'>
              <Form.Field
                width={4}
                control={TextInput}
                label={text('Name')}
                value={discount?.Name}
                onChange={(value) => changeDiscountField('Name', value)}
              />
              <Form.Field
                label={text('Hotel')}
                control={HotelsDropDown}
                value={selectedHotel}
                onChange={setSelectedHotel}
                width={4}
                search
              />
              <Form.Field
                label={text('Rates')}
                control={RatesDropDown}
                spec={ratesSpec}
                value={selectedRates}
                onChange={ratesChanged}
                width={8}
                multiple
                clearable
                search
              />
            </Form.Group>
          )}
          {
            discount?.RateDiscountAssignments && (
              <Form.Group className='discount-form'>
                <Form.Field
                  label={text("ApplyLrvFilter")}
                  value={discount?.ApplyLrvFilterTypeId || ApplyLrvFilterTypesEnum.Inherited}
                  placeholder={text("Select")}
                  control={({ ...rest }) => (
                    <DropDown data={ApplyLrvFilterTypes} {...rest} textField="Name" valueField="Id" />
                  )}
                  onChange={(value) => changeDiscountField("ApplyLrvFilterTypeId", value)}
                  width={8}
                />
                {
                  rateDiscountType === RateDiscountTypesEnum.OverallDiscount && (
                    <Form.Field
                      label={text("BookingMobilePromotion")}
                      checked={Boolean(discount?.IsMobile)}
                      control={CheckBox}
                      disabled={isEdit}
                      onChange={(value) => changeDiscountField("IsMobile", value)}
                      width={4}
                    />
                  )
                }
              </Form.Group>
            )}
          {discount?.RateDiscountAssignments && (
            <Divider style={{ opacity: 0.5 }} />
          )}
          <Form.Group className='discount-dates-form'>
            <Form.Field
              control={DatePicker}
              unstackable
              label={text('From')}
              onChange={setStartDate}
              clearable={false}
              value={startDate}
              minDate={state && moment(state.StartDate).startOf("day").toDate()}
              maxDate={state && new Date(state.EndDate)}
              width={4}
            />
            <Form.Field
              control={DatePicker}
              unstackable
              label={text('To')}
              onChange={setEndDate}
              clearable={false}
              value={endDate}
              width={4}
              minDate={state && moment(state.StartDate).startOf("day").toDate()}
              maxDate={state && new Date(state.EndDate)}
            />
            {selectedDays !== null &&
              <Form.Field
                control={WeekDaysSelect}
                onChange={setSelectedDays}
                dates={memoDates}
                useWithoutDates={!isCreate}
                initDays={isCreate ? [] : selectedDays}
              />}
          </Form.Group>
          <Message
            warning
            content={text(validateDateRangeMessage(startDate, endDate, selectedDays))}
          />
          <Form.Group className='discount-form'>
            <Form.Field
              label={text('ApplyTo')}
              control={DropDown}
              data={discountApplicationTypesDropDownData}
              value={discountApplication}
              onChange={(value) => {
                setDiscountApplication(value);
                changeDiscountField('DiscountApplicationTypeId', value);
              }
              }
              textField='Name'
              valueField='Id'
              width={4}
            />

            <Form.Field
              label={
                !discount?.RateDiscountAssignments
                  ? text('DiscountType')
                  : `${text('Promotion')} ${text('Type')}`
              }
              disabled={isEdit || existingRateDiscounts?.some(rd => rd.IsActive)}
              control={DropDown}
              data={discountTypes}
              value={discountType}
              onChange={(value) => {
                discountTypeChanged(value)
              }}
              textField='Name'
              valueField='Id'
              width={4}
            />
            {discount?.RateDiscountAssignments &&
              discountType == DiscountTypesEnum.Amount && (
                <Form.Field
                  label={text('Priceinctax')}
                  control={CheckBoxToggle}
                  onClick={() =>
                    changeDiscountField(
                      'PriceIncludesTax',
                      !discount?.PriceIncludesTax
                    )
                  }
                  checked={discount?.PriceIncludesTax}
                />
              )}
          </Form.Group>
          {discount && discount.DiscountApplicationTypeId == DiscountApplicationTypesEnum.Addon && (
            <Message visible
              success
              content={text('DiscountPerAddonInPackage')}></Message>
          )}
          {rateDiscountType == RateDiscountTypesEnum.DiscountForEveryXNight && (
            <Form.Group className='discount-form'>
              <Form.Field
                label={`${text('AppliesOn')} ${text('ReservationNight')}`}
                buttonPlacement='none'
                control={NumericInput}
                value={discount?.ApplicableNight}
                min={1}
                digits={0}
                onChange={(value) =>
                  changeDiscountField('ApplicableNight', value)
                }
                style={{ width: '10em' }}
              />
              <span style={{ paddingRight: '5em' }} />
              <Form.Field
                label={repeatEveryLabel}
                control={CheckBoxToggle}
                onClick={() =>
                  changeDiscountField(
                    'RepeatDailyDiscount',
                    !discount?.RepeatDailyDiscount
                  )
                }
                checked={discount?.RepeatDailyDiscount}
              />
            </Form.Group>
          )}
          <Divider />
          <Form.Group className='discount-designations-form' widths='equal'>
            <Form.Field
              label={text('Single')}
              buttonPlacement='none'
              control={NumericInput}
              min={currentMinInputValue}
              max={currentMaxInputValue}
              nullable
              value={getPriceByDesignationId(Designations.Single)}
              onChangeComplete={(value) =>
                changeDesignatedPrice(Designations.Single, value)
              }
            />
            <Form.Field
              label={text('Double')}
              buttonPlacement='none'
              control={NumericInput}
              min={currentMinInputValue}
              max={currentMaxInputValue}
              nullable
              value={getPriceByDesignationId(Designations.Double)}
              onChangeComplete={(value) =>
                changeDesignatedPrice(Designations.Double, value)
              }
            />
            <Form.Field
              label={`${text('Extra')} ${text('Adults')}`}
              onChange={(designationId, value) =>
                changeDesignatedPrice(designationId, value)
              }
              nullable
              min={currentMinInputValue}
              control={ExtraPersonPriceEdit}
              designationIds={adultsDesignations}
              getPriceByDesignationId={getPriceByDesignationId}
              discountType={discountType}
            />
            <Form.Field
              label={`${text('Extra')} ${text('Children')}`}
              onChange={(designationId, value) =>
                changeDesignatedPrice(designationId, value)
              }
              nullable
              min={currentMinInputValue}
              control={ExtraPersonPriceEdit}
              designationIds={childrenDesignations}
              getPriceByDesignationId={getPriceByDesignationId}
              discountType={discountType}
            />
            <Form.Field
              label={`${text('Extra')} ${text('Infants')}`}
              onChange={(designationId, value) =>
                changeDesignatedPrice(designationId, value)
              }
              nullable
              min={currentMinInputValue}
              control={ExtraPersonPriceEdit}
              designationIds={infantsDesignations}
              getPriceByDesignationId={getPriceByDesignationId}
              discountType={discountType}
            />
          </Form.Group>
          <Form.Group className='discount-designations-form'>
            <Form.Field
              label={`${text('PricingType')} - ${text('PerRoom')}`}
              buttonPlacement='none'
              control={NumericInput}
              min={currentMinInputValue}
              max={currentMaxInputValue}
              nullable
              value={getPriceByDesignationId(Designations.Room)}
              onChangeComplete={(value) =>
                changeDesignatedPrice(Designations.Room, value)
              }
              width={4}
            />
            <Form.Field
              label={text('Overall')}
              buttonPlacement='none'
              control={NumericInput}
              min={currentMinInputValue}
              max={currentMaxInputValue}
              nullable
              value={getPriceByDesignationId(Designations.Overall)}
              onChangeComplete={(value) =>
                changeDesignatedPrice(Designations.Overall, value)
              }
            />
          </Form.Group>
          {discount?.RateDiscountAssignments && (
            <Divider style={{ opacity: 0.5 }} />
          )}
          {discount?.RateDiscountAssignments && (
            <Form.Group className='discount-designations-form'>
              <Form.Field
                width={5}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <span>{text('Nights')}</span>
              </Form.Field>
              <Form.Field
                width={6}
                control={NumericInput}
                prefix={text('Min')}
                digits={0}
                buttonPlacement='right'
                onChange={(value) =>
                  changeDiscountField('MinimumNumberOfNights', value)
                }
                value={
                  discount?.MinimumNumberOfNights == null
                    ? 1
                    : discount.MinimumNumberOfNights
                }
                min={1}
                max={999}
              />
              <Form.Field
                width={6}
                control={NumericInput}
                prefix={text('Max')}
                digits={0}
                buttonPlacement='right'
                onChange={(value) =>
                  changeDiscountField('MaximumNumberOfNights', value)
                }
                value={
                  discount?.MaximumNumberOfNights == null
                    ? 999
                    : discount.MaximumNumberOfNights
                }
                min={1}
                max={999}
              />
            </Form.Group>
          )}
          {discount?.RateDiscountAssignments && (
            <Form.Group className='discount-designations-form'>
              <Form.Field
                width={5}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>{`${text('Number')} ${text('DaysBefore')} ${text(
                  'CheckIn'
                )}`}</div>
              </Form.Field>
              <Form.Field
                width={6}
                control={NumericInput}
                prefix={text('Min')}
                buttonPlacement='right'
                digits={0}
                onChange={(value) =>
                  changeDiscountField('LeadFixedDaysStart', value)
                }
                value={
                  discount?.LeadFixedDaysStart == null
                    ? 0
                    : discount.LeadFixedDaysStart
                }
                min={0}
                max={999}
              />
              <Form.Field
                width={6}
                control={NumericInput}
                prefix={text('Max')}
                buttonPlacement='right'
                digits={0}
                onChange={(value) =>
                  changeDiscountField('LeadFixedDaysEnd', value)
                }
                value={
                  discount?.LeadFixedDaysEnd == null
                    ? 999
                    : discount.LeadFixedDaysEnd
                }
                min={0}
                max={999}
              />
            </Form.Group>
          )}
          {discount?.RateDiscountAssignments && (
            <Form.Group>
              <Form.Field
                label={text('PriceGroup')}
                control={PriceGroupsDropDown}
                value={discount?.PriceGroupId}
                onChange={(value) =>
                  changeDiscountField(
                    'PriceGroupId',
                    value === '' ? null : value
                  )
                }
                width={6}
                clearable
              />
              <Form.Field
                label={text('Categories')}
                onChangeItem={(value) =>
                  changeDiscountField(
                    'ClassificationCategoriesAssignments',
                    value.map((item) => ({
                      ...item,
                      Id: 0,
                      ClassificationId: item.Id,
                      DiscountId: discount.Id,
                    }))
                  )
                }
                width={10}
                control={ClassificationCategoriesDropDown}
                multiple
                value={discount?.ClassificationCategoriesAssignments?.map(
                  (item) => item.ClassificationId
                )}
              />
            </Form.Group>
          )}
          {discount?.RateDiscountAssignments && (
            <Form.Group>
              <Form.Field
                label={text('StatisticSource')}
                onChange={(value) =>
                  changeDiscountField(
                    'StatisticSourceId',
                    value === '' ? null : value
                  )
                }
                width={6}
                control={StatisticSourcesDropDown}
                value={discount?.StatisticSourceId}
                clearable
                search
              />
              <Form.Field
                label={text("ReservationPolicy")}
                onChange={(value) => changeDiscountField("PolicyId", value === '' ? null : value)}
                control={ReservationPoliciesDropDown}
                value={discount?.PolicyId}
                spec={reservationPolicySpec}
                width={10}
                clearable
              />
            </Form.Group>
          )}

        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={handleClose} loading={isLoading}>
          {text('Close')}
        </Button>
        {isCreate && (
          <Button positive disabled={!validateDateRange(startDate, endDate, selectedDays)} onClick={handleSave} loading={isLoading} className={isCreate || discountForEdit.IsActive ? '' : 'disabled'}>
            {text('Save')}
          </Button>
        )}
        <Button positive disabled={!validateDateRange(startDate, endDate, selectedDays)} onClick={handleSaveAndClose} loading={isLoading} className={isCreate || discountForEdit.IsActive ? '' : 'disabled'}>
          {text('Saveandclose')}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

DiscountPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaveComplete: PropTypes.func,
  discountForEdit: PropTypes.any,
  rateDiscountTypeToCreate: PropTypes.any,
  isDiscountTypeDisabledBasedOnOtherRateDiscounts: PropTypes.func,
  existingRateDiscounts: PropTypes.array,
}
